import { UpdateLeadPayload } from '@/services/api/MarketingApiTypes';
import type {
  SalesforcePlan,
  SalesforceProduct,
  SalesforceRelationship,
} from '@/types/salesforce';
import type { TrackingData } from '@/utils/tracking';
import { AgeCalculatorService } from './AgeCalculatorService';
import { FormSortAnswersService } from './FormSortAnswersService';

const COUNTRY_CODE_DEFAULT = 'USA';
const RELATIONSHIP_DEFAULT: SalesforceRelationship = 'Other';
const PRODUCT_PLANS: Record<SalesforceProduct, SalesforcePlan> = {
  Signature: 'Signature',
  'Under-45': 'Under 45',
  'Over 60': 'Over 60',
};

interface BuildLeadPayloadParams {
  answersService: FormSortAnswersService;
  leadId: string;
  trackingData?: TrackingData;
  promotion?: string;
}

export function buildLeadPayload({
  answersService,
  leadId,
  trackingData,
  promotion,
}: BuildLeadPayloadParams): UpdateLeadPayload {
  const answers = answersService.getAnswers();
  if (!answers) {
    throw new Error('Form answers are required to build payload');
  }
  if (!answers.contact_details) {
    throw new Error('Contact details are required to build payload');
  }

  const product = answersService.getSelectedProduct();
  if (!product) {
    throw new Error('No valid product found');
  }

  const contactDetails = answers.contact_details;
  const primaryMember = {
    first_name: contactDetails.first_name,
    last_name: contactDetails.last_name,
    date_of_birth: answers.date_of_birth,
    relationship: 'Primary' as SalesforceRelationship,
  };
  const nonPrimaryMembers = answers.members || [];
  const extraQuestions = {
    concierge_healthcare_familiarity: answers?.concierge_healthcare_familiarity,
    healthcare_priorities: answers?.healthcare_priorities || [],
    visited_locations: answers?.visited_locations || [],
  };

  return {
    leadID: leadId,
    plan: PRODUCT_PLANS[product],
    firstName: contactDetails.first_name,
    lastName: contactDetails.last_name,
    dob: primaryMember.date_of_birth,
    zipCode: contactDetails.postal_code,
    sex: '',
    email: contactDetails.email_address,
    phoneNumber: contactDetails.phone_number,
    address: {
      street: '',
      city: '',
      state: '',
      country: COUNTRY_CODE_DEFAULT,
      zipCode: contactDetails.postal_code,
    },
    physicianName: '',
    members: nonPrimaryMembers.map(member => {
      return {
        leadId,
        firstName: member.first_name,
        lastName: member.last_name,
        dob: member.date_of_birth,
        sex: '',
        relationship: RELATIONSHIP_DEFAULT,
        email: '',
        phoneNumber: '',
        address: {
          street: '',
          city: '',
          state: '',
          country: COUNTRY_CODE_DEFAULT,
          zipCode: contactDetails.postal_code,
        },
        physicianName: '',
        hasPediatrics: AgeCalculatorService.isPediatric(
          new Date(member.date_of_birth)
        ),
      };
    }),
    promotion,
    submissionUrl: trackingData?.submission_url ?? '',
    ...extraQuestions,
    ...trackingData,
  };
}
